import type { CommandeStatut } from './commande.ts';
import type { ProductType } from './product.ts';
import type { RaisonMouvement } from './mouvement.ts';

/** Seuil de stock faible — la constante unique du tableau de bord. */
export const SEUIL_STOCK_FAIBLE = 3;

/** Nombre de mois affichés par le graphique de chiffre d'affaires. */
export const MOIS_HISTORIQUE = 12;

export type StatsDashboard = {
  valeur_stock_initial: number;
  valeur_stock_vente: number;
  nombre_articles: number;
  /** Somme de `commandes.total` du mois, hors ANNULEE. Les factures ne comptent pas. */
  ca_mois: number;
  /** Somme des paiements du mois. */
  encaisse_mois: number;
  /** Commandes **et** ventes au comptoir : le CA du mois réunit les deux. */
  operations_mois: number;
  /** Somme des restes dus, toutes commandes non annulées confondues. */
  total_restes: number;
  commandes_non_soldees: number;
  articles_stock_faible: number;
  commandes_a_verifier: number;
  /** Clients atteignant le délai AMO dans le mois courant. */
  renouvellements_mois: number;
  /**
   * Mêmes mesures, sur les **mêmes jours écoulés** du mois précédent.
   *
   * Le mois en cours est incomplet : le 10 du mois, l'opposer à un mois entier
   * annoncerait une chute de deux tiers sans que rien ait bougé. La
   * comparaison porte donc sur le 1er au `jours_ecoules` de chaque mois.
   */
  ca_periode_precedente: number;
  encaisse_periode_precedente: number;
  /** Jours écoulés du mois courant — le périmètre que la comparaison couvre. */
  jours_ecoules: number;
};

export type LigneStockFaible = {
  id: string;
  nom: string;
  type: ProductType;
  quantite: number;
  prix_vente: number;
};

export type PointCaMensuel = {
  /** `YYYY-MM`. */
  mois: string;
  /** Libellé court en français, ex. « janv. 26 ». */
  libelle: string;
  ca: number;
  encaisse: number;
  commandes: number;
};

export type LigneTop = {
  libelle: string;
  quantite: number;
  montant: number;
};

export type MouvementRecent = {
  id: string;
  product_id: string;
  produit_nom: string;
  delta: number;
  raison: RaisonMouvement;
  note: string | null;
  created_at: string;
};

export type CommandeRecente = {
  id: string;
  date_commande: string;
  client_nom: string;
  total: number;
  reste: number;
  statut: CommandeStatut;
};

/**
 * Client arrivant au terme du délai AMO dans le mois courant.
 *
 * `depart_estime` est le point délicat : aucune des commandes reprises du
 * cahier ne porte de date d'ordonnance — l'import ne l'a jamais relevée. Le
 * délai part alors de la date de commande, qui suit la prescription de
 * quelques jours. Sur deux ans l'écart est négligeable, mais l'écran doit le
 * dire : c'est une approximation, pas une date de prescription.
 */
export type LigneRenouvellement = {
  client_id: string;
  client_nom: string;
  tel: string | null;
  /** Date retenue comme départ du délai, `YYYY-MM-DD`. */
  depart: string;
  /** Vrai quand `depart` est la date de commande, faute d'ordonnance datée. */
  depart_estime: boolean;
  /** Premier jour où l'AMO peut reprendre un équipement. */
  echeance: string;
  /** Nombre de commandes non annulées de ce client. */
  commandes: number;
};

/** Charge utile unique renvoyée par `GET /api/v1/dashboard`. */
export type Dashboard = {
  stats: StatsDashboard;
  stock_faible: LigneStockFaible[];
  ca_par_mois: PointCaMensuel[];
  top_verres: LigneTop[];
  top_montures: LigneTop[];
  par_societe: LigneTop[];
  derniers_mouvements: MouvementRecent[];
  dernieres_commandes: CommandeRecente[];
  /** Liste de rappel : les premiers du mois, le total est dans `stats`. */
  renouvellements: LigneRenouvellement[];
};
